import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Database, HeartPulse, Brain, BarChart3,
  ArrowRight, Check, Layers, Sparkles, LineChart
} from "lucide-react";

const ARCH_LAYERS = [
  {
    level: 4,
    label: "Executive Decision Intelligence",
    sublabel: "Fast Track Data Science & Digital Twin",
    icon: LineChart,
    color: "#7EA13E",
    items: [
      "Executive Control Tower — real-time strategic dashboard",
      "Digital Twin — simulate and evaluate strategic decisions",
      "Predictive revenue and demand forecasting",
      "Operational change simulation",
    ],
  },
  {
    level: 3,
    label: "Intelligence & Automation",
    sublabel: "AI Design Factory for SMEs",
    icon: Brain,
    color: "#E5BD77",
    items: [
      "Demand forecasting models",
      "Customer behaviour analysis",
      "Automated workflow optimisation",
      "Predictive operational insights",
    ],
  },
  {
    level: 2,
    label: "Industry Solutions",
    sublabel: "Long-Term Care Patient Accounting & Billing",
    icon: HeartPulse,
    color: "#E95D54",
    items: [
      "Automated patient billing workflows",
      "Subsidy & government funding management",
      "Patient financial tracking",
      "Compliance-ready reporting",
    ],
  },
  {
    level: 1,
    label: "Operational Core",
    sublabel: "SME ERP Foundation (Business Central)",
    icon: Database,
    color: "#308795",
    items: [
      "Accounting & financial management",
      "Procurement & vendor management",
      "Sales & customer billing",
      "Inventory & supply chain",
    ],
  },
];

const OUTCOMES = [
  "Operate on a unified data platform",
  "Automate critical workflows",
  "Apply AI to operational challenges",
  "Empower executives with real-time intelligence",
];

export const SMEArchitectureSection = () => {
  return (
    <section id="stack" className="py-24 lg:py-32 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-6">
          <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">
            Technology Architecture
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">
            The Intelligent SME Architecture
          </h2>
          <p className="text-[#6B6567] text-lg leading-relaxed">
            Building the Digital Operating System for Modern Businesses — a layered
            framework that connects operations, intelligence, and decision-making.
          </p>
        </div>

        {/* Architecture Visual */}
        <div className="max-w-4xl mx-auto mb-16">
          {/* Layered stack — top to bottom */}
          <div className="space-y-0">
            {ARCH_LAYERS.map((layer, i) => {
              const Icon = layer.icon;
              const isFirst = i === 0;
              const isLast = i === ARCH_LAYERS.length - 1;

              return (
                <div
                  key={layer.level}
                  className="group relative transition-all duration-400 hover:z-10"
                >
                  {/* Layer card */}
                  <div
                    className={`relative overflow-hidden border-x-2 border-t-2 ${isLast ? "border-b-2 rounded-b-2xl" : ""} ${isFirst ? "rounded-t-2xl" : ""} bg-white transition-all duration-300 hover:shadow-lg`}
                    style={{ borderColor: layer.color + "30" }}
                  >
                    {/* Left accent */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1.5"
                      style={{ backgroundColor: layer.color }}
                    />

                    <div className="pl-7 pr-6 py-6 lg:py-7">
                      <div className="lg:flex lg:items-start lg:gap-8">
                        {/* Left — Label */}
                        <div className="lg:w-[320px] shrink-0 mb-4 lg:mb-0">
                          <div className="flex items-center gap-3 mb-2">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                              style={{ backgroundColor: layer.color + "15" }}
                            >
                              <Icon className="w-5 h-5" style={{ color: layer.color }} />
                            </div>
                            <span
                              className="text-[10px] font-bold uppercase tracking-[0.15em]"
                              style={{ color: layer.color }}
                            >
                              Layer {layer.level}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-[#2C2A2B] leading-snug">
                            {layer.label}
                          </h3>
                          <p className="text-xs text-[#8A8385] mt-1">{layer.sublabel}</p>
                        </div>

                        {/* Right — Items */}
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {layer.items.map((item, j) => (
                            <div key={j} className="flex items-start gap-2">
                              <Check
                                className="w-3.5 h-3.5 mt-0.5 shrink-0"
                                style={{ color: layer.color }}
                              />
                              <span className="text-xs text-[#494546] leading-snug">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Connector arrow between layers */}
                  {!isLast && (
                    <div className="flex justify-center -my-px">
                      <div className="w-px h-2" style={{ backgroundColor: layer.color + "30" }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Caption */}
          <p className="text-center text-xs text-[#8A8385] mt-4 italic">
            The Intelligent SME Architecture — powering operations, intelligence, and decision-making.
          </p>
        </div>

        {/* Outcomes */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-[#2C2A2B] mb-3">
              A Connected Digital Future for SMEs
            </h3>
            <p className="text-sm text-[#6B6567] leading-relaxed">
              When these four layers work together, SMEs gain a powerful digital platform
              that supports both operational efficiency and strategic growth.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
            {OUTCOMES.map((o, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-white border border-[#E0D1BB]/30 rounded-xl px-5 py-3.5 transition-all duration-300 hover:shadow-sm"
              >
                <Check className="w-4 h-4 text-[#308795] shrink-0" />
                <span className="text-sm font-medium text-[#2C2A2B]">{o}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/consultation" onClick={() => window.scrollTo(0, 0)}>
              <Button className="bg-[#308795] hover:bg-[#267580] text-white font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#308795]/20 hover:-translate-y-0.5 group">
                Speak with Our Digital Transformation Specialists
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
